import axios from 'axios';
import { createAuthenticatedClient } from './AuthenticatedClient';
import { DeviationPreview, NoteDetail, ReweightingReferences, StatusFilter, SubmissionResult, WeightLineInput, Worklist } from '../Models/ReWeightingModels/Workspace';

const configuredBase = process.env.REACT_APP_API_BASE_URL_REWEIGHTING?.replace(/\/+$/, '');
const baseUrl = configuredBase?.endsWith('/v1') ? configuredBase : configuredBase ? `${configuredBase}/v1` : undefined;
const client = createAuthenticatedClient({ baseURL: baseUrl, timeout: 45000 });
client.interceptors.request.use(async request => {
  if (!baseUrl) throw new Error('Tenging við endurvigtunarþjónustu hefur ekki verið stillt.');
  return request;
});

export async function fetchReweightingNotes(status: StatusFilter, search: string, page: number, signal?: AbortSignal) {
  return (await client.get<Worklist>('/weight-notes', { params: { status, search, page, pageSize: 25 }, signal })).data;
}
export async function fetchReweightingNote(id: number, signal?: AbortSignal) {
  return (await client.get<NoteDetail>(`/weight-notes/${id}`, { signal })).data;
}
export async function fetchReweightingReferences(signal?: AbortSignal) {
  return (await client.get<ReweightingReferences>('/reference-data', { signal })).data;
}
export async function saveReweightingNote(detail: NoteDetail, weights: WeightLineInput[]) {
  return (await client.put<NoteDetail>(`/weight-notes/${detail.note.id}/weights`, { weights },
    { headers: { 'If-Match': `"${detail.version}"` } })).data;
}
export async function previewReweightingNote(detail: NoteDetail) {
  return (await client.post<DeviationPreview>(`/weight-notes/${detail.note.id}/preview`, undefined,
    { headers: { 'If-Match': `"${detail.version}"` } })).data;
}
export async function submitReweightingNote(noteId: number, version: string, deviationReason: string) {
  return (await client.post<SubmissionResult>(`/weight-notes/${noteId}/submit`, { deviationReason: deviationReason.trim() || null },
    { headers: { 'If-Match': `"${version}"` } })).data;
}
export async function uploadReweightingAttachment(detail: NoteDetail, file: File) {
  const body = new FormData();
  body.append('file', file);
  return (await client.put<NoteDetail>(`/weight-notes/${detail.note.id}/attachment`, body,
    { headers: { 'If-Match': `"${detail.version}"` }, timeout: 120000 })).data;
}
async function downloadDocument(path: string): Promise<Blob> {
  try { return (await client.get<Blob>(path, { responseType: 'blob' })).data; }
  catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try { error.response.data = JSON.parse(text); } catch { error.response.data = { title: text }; }
    }
    throw new Error(reweightingError(error));
  }
}
export const downloadReweightingAttachment = (id: number) => downloadDocument(`/weight-notes/${id}/attachment`);
export const downloadReweightingPdf = (id: number, sourceId?: number) => downloadDocument(sourceId === undefined
  ? `/weight-notes/${id}/pdf` : `/weight-notes/${id}/source-notes/${sourceId}/pdf`);
export const isCancelled = (error: unknown) => axios.isCancel(error);
export const needsReload = (error: unknown) => axios.isAxiosError(error) && (!error.response || [409, 412].includes(error.response.status));
export function reweightingError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data?.title === 'string') return data.title;
    if (error.response?.status === 401) return 'Endurvigtunarþjónustan hafnaði innskráningunni. Skráðu þig inn aftur. Ef villan endurtekur sig þarf að yfirfara aðgangsstillingar þjónustunnar.';
    return 'Ekki náðist samband við endurvigtunarþjónustu. Reyndu aftur.';
  }
  return error instanceof Error ? error.message : 'Óvænt villa kom upp. Reyndu aftur.';
}
